/**
 * ReviewWorkspace — every proposed change, one at a time, at a size you can
 * read: what it fixes, where it came from, and exactly what it changes, as
 * marked-up changes, before and after side by side, or the new text alone.
 *
 * Pure view of a ``ReviewState`` — it renders in the review window (an Office
 * dialog) and, where dialogs are unavailable, in a full-pane overlay. Every
 * click goes out through a callback; the task pane applies it.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import remarkGfm from "remark-gfm";
import { htmlTablesAsText } from "../../services/formatter/htmlTable";
import { MarkdownWithTables } from "../components/MarkdownWithTables";
import { TableDiffView } from "../components/TableDiffView";
import { diffSummary, type DiffSegment, wordDiff } from "../services/wordDiff";
import { Button, Eyebrow, LinkButton, Notice, Segmented, StatusPill, TextField } from "../../ds/components";
import type { ReviewItem, ReviewState } from "./reviewProtocol";
import { SizeToggle, type ViewPlacement } from "./SizeToggle";

const SERIF = "'Source Serif 4', Georgia, 'Times New Roman', serif";

export type ReviewView = "changes" | "side" | "new";

export interface ReviewWorkspaceProps {
  state: ReviewState;
  onSelect: (id: string) => void;
  /** ``content``: the writer's edit of the proposed text, when they edited it. */
  onApprove: (id: string, content?: string) => void;
  onReject: (id: string) => void;
  onRevise: (id: string, guidance: string) => void;
  onApproveAll: () => void;
  onStopAll: () => void;
  /** Undo the change just approved (shown while ``state.undoSection`` is set). */
  onUndo?: () => void;
  onClose: () => void;
  /** Where this view is showing, and the Expand / Minimise switch (absent when
   *  this Word cannot open a window). */
  placement?: ViewPlacement;
  onResize?: () => void;
}

// Layout only; colour, type and controls come from the design system
// (ds/asthra.css tokens and ds/components).
const useStyles = makeStyles({
  root: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--surface)",
    color: "var(--ink)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
  },
  top: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    borderBottom: "1px solid var(--line)",
  },
  title: { margin: 0, fontSize: "15px", lineHeight: "20px", fontWeight: 600 },
  meta: { fontSize: "12px", color: "var(--ink-muted)" },
  undoBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 16px",
    fontSize: "12px",
    color: "var(--ink-muted)",
    backgroundColor: "var(--surface-sunken)",
    borderBottom: "1px solid var(--line)",
  },
  spacer: { flex: 1 },
  main: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    "@media (max-width: 640px)": { flexDirection: "column" },
  },
  list: {
    width: "280px",
    flexShrink: 0,
    overflowY: "auto",
    borderRight: "1px solid var(--line)",
    backgroundColor: "var(--surface-sunken)",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    "@media (max-width: 640px)": {
      width: "auto",
      maxHeight: "132px",
      borderRight: "none",
      borderBottom: "1px solid var(--line)",
    },
  },
  row: {
    textAlign: "left",
    border: "1px solid transparent",
    borderRadius: "var(--radius-md)",
    background: "transparent",
    padding: "8px 10px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    font: "inherit",
    color: "var(--ink)",
    ":hover": { backgroundColor: "var(--surface)" },
    ":disabled": { cursor: "default", opacity: 0.6 },
  },
  rowActive: {
    backgroundColor: "var(--brand-soft)",
  },
  rowHead: { fontSize: "13px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: {
    fontSize: "12px",
    color: "var(--ink-muted)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  detail: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    // A failed Approve sits right above the buttons.
    "& > .as-notice": { margin: "8px 16px 0" },
  },
  detailScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  detailHead: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
  number: { fontFamily: "var(--font-mono)", fontSize: "13px", fontWeight: 600, color: "var(--ink-muted)" },
  sectionTitle: { fontSize: "15px", fontWeight: 600 },
  facts: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: "12px",
    rowGap: "6px",
    alignItems: "baseline",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  factList: { margin: 0, paddingLeft: "16px", color: "var(--ink)" },
  stillOpen: { color: "var(--danger)" },
  doc: {
    fontFamily: SERIF,
    fontSize: "14px",
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    padding: "12px 14px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)",
    backgroundColor: "var(--surface)",
  },
  docMd: {
    whiteSpace: "normal",
    "& table": { borderCollapse: "collapse", fontFamily: "var(--font-sans)", fontSize: "12px", margin: "8px 0" },
    "& th, & td": { border: "1px solid var(--line-strong)", padding: "4px 8px", verticalAlign: "top" },
    "& p": { margin: "0 0 10px" },
  },
  sideBySide: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (max-width: 640px)": { gridTemplateColumns: "1fr" },
  },
  colHead: { marginBottom: "4px" },
  ins: {
    backgroundColor: "var(--success-soft)",
    color: "var(--success)",
    textDecorationLine: "underline",
    textUnderlineOffset: "2px",
    borderRadius: "2px",
  },
  del: {
    backgroundColor: "var(--danger-soft)",
    color: "var(--danger)",
    textDecorationLine: "line-through",
    borderRadius: "2px",
  },
  summary: { fontSize: "12px", color: "var(--ink-muted)" },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "64px",
    resize: "vertical",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    padding: "8px 10px",
    color: "var(--ink)",
    backgroundColor: "var(--surface)",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--line-strong)",
  },
  editArea: { minHeight: "220px", fontFamily: SERIF, fontSize: "14px", lineHeight: 1.5 },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    borderTop: "1px solid var(--line)",
    backgroundColor: "var(--surface)",
    flexWrap: "wrap",
  },
  empty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "var(--ink-muted)",
  },
});

function words(n: number): string {
  return `${n} word${n === 1 ? "" : "s"}`;
}

/** Status in words, for the pill beside the section heading. */
export function statusLabel(state: ReviewState, item: ReviewItem): string {
  if (item.id !== state.activeId) return "Awaiting you";
  if (state.phase === "applying") return "Applying";
  if (state.phase === "revising") return "Rewriting";
  return "Awaiting you";
}

const Segments: React.FC<{ segments: readonly DiffSegment[] }> = ({ segments }) => {
  const styles = useStyles();
  return (
    <>
      {segments.map((s, i) => {
        // Diff segments are positional and never reorder.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
        if (s.kind === "same") return <span key={i}>{s.text}</span>;
        if (s.kind === "added") {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
            <ins key={i} className={styles.ins}>
              {s.text}
            </ins>
          );
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
          <del key={i} className={styles.del}>
            {s.text}
          </del>
        );
      })}
    </>
  );
};

/** A figure pointer, as a reader sees it: "[Figure: <caption>]". */
export function showFigures(text: string): string {
  return text.replace(/\[IMAGE DATA:\s*([^\]]+)\]/g, "[Figure: $1]");
}

export const ChangeBody: React.FC<{ item: ReviewItem; view: ReviewView }> = ({ item, view }) => {
  const styles = useStyles();
  const before = showFigures(item.before);
  const after = showFigures(item.after);
  const segments = useMemo(() => wordDiff(htmlTablesAsText(before), htmlTablesAsText(after)), [before, after]);
  const { added, removed } = diffSummary(segments);

  if (item.kind === "field") {
    return (
      <div className={styles.field}>
        <Eyebrow as="h3">{item.fieldLabel}</Eyebrow>
        <div className={styles.doc}>
          <del className={styles.del}>{item.before || "(empty)"}</del>
          {"  "}
          <ins className={styles.ins}>{item.after}</ins>
        </div>
      </div>
    );
  }
  if (item.kind === "table" && item.table) {
    return <TableDiffView snippet={item.table} />;
  }
  if (view === "new") {
    return (
      <div className={mergeClasses(styles.doc, styles.docMd)}>
        <MarkdownWithTables remarkPlugins={[remarkGfm]}>{after}</MarkdownWithTables>
      </div>
    );
  }
  // Before and after: the two texts as they read, no marks. "Show changes"
  // is where the differences are marked.
  if (view === "side") {
    return (
      <div className={styles.sideBySide}>
        <div>
          <div className={styles.colHead}>
            <Eyebrow as="h3">Before</Eyebrow>
          </div>
          <div className={mergeClasses(styles.doc, styles.docMd)}>
            <MarkdownWithTables remarkPlugins={[remarkGfm]}>{before}</MarkdownWithTables>
          </div>
        </div>
        <div>
          <div className={styles.colHead}>
            <Eyebrow as="h3">After</Eyebrow>
          </div>
          <div className={mergeClasses(styles.doc, styles.docMd)}>
            <MarkdownWithTables remarkPlugins={[remarkGfm]}>{after}</MarkdownWithTables>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className={styles.doc}>
        <Segments segments={segments} />
      </div>
      <div className={styles.summary}>
        Struck = removed ({words(removed)}) · Underlined = added ({words(added)})
      </div>
    </>
  );
};

export const ReviewWorkspace: React.FC<ReviewWorkspaceProps> = ({
  state,
  onSelect,
  onApprove,
  onReject,
  onRevise,
  onApproveAll,
  onStopAll,
  onUndo,
  onClose,
  placement = "pane",
  onResize,
}) => {
  const styles = useStyles();
  const [view, setView] = useState<ReviewView>("changes");
  const [reviseOpen, setReviseOpen] = useState(false);
  const [guidance, setGuidance] = useState("");
  // "Edit text": the writer's own wording, approved in place of the proposal.
  const [editText, setEditText] = useState<string | null>(null);
  const active = state.items.find((i) => i.id === state.activeId) ?? state.items[0] ?? null;
  const busy = state.phase === "applying" || state.phase === "revising" || state.approvingAll;
  const count = state.items.length;

  // A new change under review starts with a closed Revise box.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the active change only
  useEffect(() => {
    setReviseOpen(false);
    setGuidance("");
    setEditText(null);
  }, [active?.id]);

  // "Change applied · Undo" — after an approval, for a few seconds.
  const undoBar =
    state.undoSection && onUndo ? (
      <div className={styles.undoBar} role="status">
        <span>Change applied in §{state.undoSection}.</span>
        <LinkButton onClick={onUndo}>Undo</LinkButton>
      </div>
    ) : null;

  if (!active) {
    return (
      <div className={styles.root}>
        {undoBar}
        <div className={styles.empty}>
          <h1 className={styles.title}>{state.phase === "loading" ? "Loading changes" : "Nothing left to review"}</h1>
          {state.error ? <Notice tone="danger">{state.error}</Notice> : null}
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const sendRevise = () => {
    const g = guidance.trim();
    if (!g) return;
    onRevise(active.id, g);
    setReviseOpen(false);
    setGuidance("");
  };

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <h1 className={styles.title}>Review changes</h1>
        <span className={styles.meta}>
          {count} waiting{state.newCount ? ` · ${state.newCount} new` : ""}
        </span>
        <span className={styles.spacer} />
        <SizeToggle placement={placement} onToggle={onResize} />
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {undoBar}

      <div className={styles.main}>
        <nav className={styles.list} aria-label="Changes">
          {state.items.map((item) => (
            <button
              type="button"
              key={item.id}
              className={mergeClasses(styles.row, item.id === active.id && styles.rowActive)}
              aria-current={item.id === active.id ? "true" : undefined}
              onClick={() => onSelect(item.id)}
              disabled={busy && item.id !== active.id}
            >
              <span className={styles.rowHead}>
                {item.kind === "field"
                  ? `Title page · ${item.fieldLabel}`
                  : `${item.sectionNumber} ${item.sectionTitle}`}
              </span>
              <span className={styles.rowSub}>
                {item.sourceNote ?? (item.fills.length > 0 ? `Fixes: ${item.fills[0]}` : "Rewrite")}
              </span>
            </button>
          ))}
        </nav>

        <section className={styles.detail} aria-label="Change under review">
          <div className={styles.detailScroll}>
            <div className={styles.detailHead}>
              <span className={styles.number}>{active.kind === "field" ? "Title page" : active.sectionNumber}</span>
              <span className={styles.sectionTitle}>
                {active.kind === "field" ? active.fieldLabel : active.sectionTitle}
              </span>
              <StatusPill status={busy ? "review" : "paused"}>{statusLabel(state, active)}</StatusPill>
            </div>

            <div className={styles.facts}>
              {active.sourceNote ? (
                <>
                  <Eyebrow as="h3">Source</Eyebrow>
                  <span>{active.sourceNote}</span>
                </>
              ) : null}
              {active.fills.length > 0 ? (
                <>
                  <Eyebrow as="h3">Fixes</Eyebrow>
                  <ul className={styles.factList}>
                    {active.fills.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </>
              ) : null}
              {active.stillOpen.length > 0 ? (
                <>
                  <Eyebrow as="h3">Still open</Eyebrow>
                  <ul className={mergeClasses(styles.factList, styles.stillOpen)}>
                    {active.stillOpen.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>

            {active.kind === "text" ? (
              <Segmented<ReviewView>
                label="View"
                value={view}
                onChange={setView}
                items={[
                  { id: "changes", label: "Show changes" },
                  { id: "side", label: "Before and after" },
                  { id: "new", label: "New text" },
                ]}
              />
            ) : null}

            {editText !== null ? (
              <textarea
                className={mergeClasses(styles.textarea, styles.editArea)}
                aria-label="Edit the new text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                // biome-ignore lint/a11y/noAutofocus: focus the box the writer just opened
                autoFocus
              />
            ) : (
              <ChangeBody item={active} view={view} />
            )}

            {state.phase === "revising" && state.progressLabel ? (
              <p className="as-note">{state.progressLabel}</p>
            ) : null}

            {reviseOpen ? (
              <TextField
                id="review-revise-guidance"
                label="What should be different?"
                multiline
                value={guidance}
                onChange={setGuidance}
                placeholder="Say how this change should be different"
              />
            ) : null}
          </div>

          {/* Next to the buttons, not under the change: below a long diff a failed
              Approve looked like nothing happened. */}
          {state.error ? (
            <Notice tone="danger">{state.error}</Notice>
          ) : null}
          <div className={styles.footer}>
            {state.approvingAll ? (
              <Button onClick={onStopAll}>Stop approving</Button>
            ) : count > 1 ? (
              <Button onClick={onApproveAll} disabled={busy}>
                Approve all {count}
              </Button>
            ) : null}
            <span className="as-note">Nothing in Word changes until you approve.</span>
            <span className={styles.spacer} />
            {editText !== null ? (
              <>
                <Button variant="ghost" onClick={() => setEditText(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => onApprove(active.id, editText === active.after ? undefined : editText)}
                  disabled={busy || !editText.trim()}
                >
                  {state.phase === "applying" ? "Applying" : "Approve edited text"}
                </Button>
              </>
            ) : reviseOpen ? (
              <>
                <Button variant="ghost" onClick={() => setReviseOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={sendRevise} disabled={busy || !guidance.trim()}>
                  Rewrite
                </Button>
              </>
            ) : (
              <>
                {active.kind === "text" && state.canRevise !== false ? (
                  <Button variant="ghost" onClick={() => setReviseOpen(true)} disabled={busy}>
                    Revise
                  </Button>
                ) : null}
                {active.kind === "text" && state.canEdit ? (
                  <Button variant="ghost" onClick={() => setEditText(active.after)} disabled={busy}>
                    Edit text
                  </Button>
                ) : null}
                <Button onClick={() => onReject(active.id)} disabled={busy}>
                  Reject
                </Button>
                <Button variant="primary" onClick={() => onApprove(active.id)} disabled={busy}>
                  {state.phase === "applying" ? "Applying" : "Approve"}
                </Button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReviewWorkspace;
